import React, { useEffect } from 'react';
import { BrowserRouter, useLocation } from 'react-router-dom';

import './services/translations';

import { Provider as ReduxProvider, useDispatch, useSelector } from 'react-redux';
import { BeamProvider, ToastContainer } from '@vst/beam';
import Cookies from 'js-cookie';

import { AppRouter } from '@app/router/router';
import FullPageLoading from '@app/views/FullPageLoading';
import { Maintenance } from '@app/views/Maintenance';
import RegistrationProvider from '@app/views/Registration';
import TermsAndPushContainer from '@app/views/TermsAndPushNotifications';
import { ServerError } from '@app/components/ServerError';
import { useMobileAppEvents, useTimeout, useToken } from '@app/hooks';
import { REDIRECT_PATH_COOKIE_NAME, selectLocale, selectUser, store } from '@app/redux';
import { getMaintenanceStatus, selectConfig, setConfig } from '@app/redux/config';

import packageJson from '../../../myversion.json';
import { ApolloProviders, I18nLocaleProvider, WebAuthProvider } from './providers';
import { ScriptId } from './services/adobeDataLayer';
import { loadAdobeTargetScript } from './services/adobeDataLayer/loadAdobeTargetScript';
import i18n from './services/translations';

import env from './env/env';

//How long we should wait for auth MFE to loads
const AUTH_MFE_TIMEOUT_MS = 20000;

const App = (): JSX.Element => {
  const { pathname } = useLocation();
  const { user } = useSelector(selectUser);
  const { inMaintenance, loadingConfig } = useSelector(selectConfig);
  const { isAuthenticated, isAuthMFELoaded } = user.auth;
  const {
    locale: { userLocale },
  } = useSelector(selectLocale);
  const dispatch = useDispatch();
  const { login } = useToken();

  useMobileAppEvents();

  useEffect(() => {
    if (isAuthenticated && userLocale) {
      dispatch(setConfig({ version: packageJson.version }));
    }
  }, [isAuthenticated, userLocale]);

  useEffect((): void => {
    const redirectPath = Cookies.get(REDIRECT_PATH_COOKIE_NAME);
    if (!redirectPath) {
      Cookies.set(REDIRECT_PATH_COOKIE_NAME, pathname);
    }
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    if (!isAuthMFELoaded && inMaintenance === null) {
      dispatch(getMaintenanceStatus());
    }

    if (inMaintenance !== null && inMaintenance === false) {
      if (isAuthMFELoaded) login(window.location.href);
    }

    if (inMaintenance) {
      i18n.changeLanguage(navigator.language);
    }
  }, [isAuthMFELoaded, inMaintenance]);

  useEffect(() => {
    const loadAdobeTarget = async () => {
      try {
        await loadAdobeTargetScript(env.adobeTargetScript, ScriptId.ADOBE_TARGET);
      } catch (error) {
        console.error('Error loading script:', error);
      }
    };

    loadAdobeTarget();
  }, []);

  const [authTimeout, setAuthTimeout] = React.useState(false);

  useTimeout(() => setAuthTimeout(true), AUTH_MFE_TIMEOUT_MS);

  if (inMaintenance) {
    return <Maintenance />;
  }

  if (!isAuthenticated && authTimeout) {
    return <ServerError />;
  }

  const noProductKind = user.productKind === null;

  if (!isAuthenticated || noProductKind || loadingConfig) {
    return <FullPageLoading />;
  }

  return (
    <TermsAndPushContainer>
      <AppRouter />
    </TermsAndPushContainer>
  );
};

const AppWrapper = (): JSX.Element => {
  return (
    <ReduxProvider store={store}>
      <I18nLocaleProvider>
        <BrowserRouter>
          <WebAuthProvider>
            <ApolloProviders URI={env.backEndUrls.backEndUrl}>
              <RegistrationProvider>
                <BeamProvider withToastProvider>
                  <ToastContainer className={'toast-position-override'} position={'top-right'} />
                  <App />
                </BeamProvider>
              </RegistrationProvider>
            </ApolloProviders>
          </WebAuthProvider>
        </BrowserRouter>
      </I18nLocaleProvider>
    </ReduxProvider>
  );
};

export default AppWrapper;
